import ApiService from './api.service'
import { isPasswordValid } from '@/utils/passwordValidation'

/**
 * DTO for checking if an email exists
 */
export interface EmailCheckDto {
  email: string
  exists: boolean
}

/**
 * DTO for requesting an OTP
 */
export interface OtpRequestDto {
  email: string
}

/**
 * DTO for verifying an OTP
 */
export interface OtpVerificationDto {
  email: string
  otp: string
}

/**
 * DTO for signup request
 */
export interface SignupRequestDto {
  email: string
  password: string
  confirmPassword: string
  firstName: string
  lastName: string
  phone?: string
  billingPeriod?: string
}

export interface AccountSignupRequestDto {
  firstName: string
  lastName: string
  email: string
  password: string
  type: 'CLIENT' | 'FIRM_MEMBER' | 'FIRM_OWNER'
  securityCode?: string
  acceptedTerms: boolean
  termsVersion: string
}

/**
 * DTO for signup response
 */
export interface SignupResponseDto {
  userId: number
  login: string
  email: string
  type: string
  message: string
}

/**
 * Generic API response wrapper
 */
export interface ApiResponseDto<T> {
  success: boolean
  message: string
  data?: T
}

/**
 * Service for handling user signup operations
 */
class SignupService extends ApiService {
  private readonly BASE_URL = '/public/api/signup'

  constructor() {
    // Enable automatic notifications for signup service errors
    super({
      showNotifications: true,
      notificationOptions: {
        title: 'Signup Error'
      }
    })
  }

  /**
   * Override getHeaders to exclude authorization header for public signup endpoints
   */
  getHeaders(customHeaders: Record<string, string> = {}): Record<string, string> {
    // Return only Content-Type and custom headers, no Authorization header
    return {
      'Content-Type': 'application/json',
      ...customHeaders
    }
  }

  /**
   * Register a new user
   * @param signupRequest - The signup data
   * @returns Promise that resolves with the signup response containing userId
   */
  async signup(signupRequest: SignupRequestDto): Promise<SignupResponseDto> {
    const response = await this.post(this.BASE_URL, signupRequest)
    return response as SignupResponseDto
  }

  async createAccount(request: AccountSignupRequestDto): Promise<SignupResponseDto> {
    return await this.post(`${this.BASE_URL}/account`, request) as SignupResponseDto
  }

  /**
   * Check if an email already exists in the system
   * @param email - The email to check
   * @returns Promise that resolves with EmailCheckDto containing email and exists flag
   */
  async checkEmailExists(email: string): Promise<EmailCheckDto> {
    const response = await this.get(`${this.BASE_URL}/email/exists?email=${encodeURIComponent(email)}`)
    return response as EmailCheckDto
  }

  /**
   * Check if a phone number already exists in the system
   * @param phone - The phone number to check
   * @returns Promise that resolves with EmailCheckDto containing phone and exists flag
   */
  async checkPhoneExists(phone: string): Promise<EmailCheckDto> {
    const response = await this.get(`${this.BASE_URL}/phone/exists?phone=${encodeURIComponent(phone)}`)
    return response as EmailCheckDto
  }

  /**
   * Check if firm email already exists in the system
   * @param email - The email to check
   * @returns Promise that resolves with EmailCheckDto containing email and exists flag
   */
  async checkFirmEmailExists(email: string): Promise<EmailCheckDto> {
    const response = await this.get(`${this.BASE_URL}/firm/email/exists?email=${encodeURIComponent(email)}`)
    return response as EmailCheckDto
  }

  /**
   * Check if firm name already exists in the system
   * @param name - The firm name to check
   * @returns Promise that resolves with EmailCheckDto containing name and exists flag
   */
  async checkFirmNameExists(name: string): Promise<EmailCheckDto> {
    const response = await this.get(`${this.BASE_URL}/firm/exists?firm=${encodeURIComponent(name)}`)
    return response as EmailCheckDto
  }

  /**
   * Check if firm phone number already exists in the system
   * @param phone - The phone number to check
   * @returns Promise that resolves with EmailCheckDto containing phone and exists flag
   */
  async checkFirmPhoneExists(phone: string): Promise<EmailCheckDto> {
    const response = await this.get(`${this.BASE_URL}/firm/phone/exists?phone=${encodeURIComponent(phone)}`)
    return response as EmailCheckDto
  }

  /**
   * Generate and send a new OTP for email verification
   * @param email - The email to send OTP to
   * @param language - The language for the email (default: 'en')
   * @returns Promise that resolves with API response
   */
  async sendOtp(email: string, language: string = 'en'): Promise<ApiResponseDto<void>> {
    const otpRequest: OtpRequestDto = { email }
    const response = await this.post(
      `${this.BASE_URL}/email/verification/send-otp`,
      otpRequest,
      {
        params: { language }
      }
    )
    return response as ApiResponseDto<void>
  }

  /**
   * Verify the OTP provided by the user
   * @param email - The email address
   * @param otp - The OTP code to verify
   * @returns Promise that resolves with API response indicating verification success
   */
  async verifyOtp(email: string, otp: string): Promise<ApiResponseDto<void>> {
    const verificationRequest: OtpVerificationDto = { email, otp }
    const response = await this.post(
      `${this.BASE_URL}/email/verification/verify-otp`,
      verificationRequest
    )
    return response as ApiResponseDto<void>
  }

  /**
   * Validate email format
   * @param email - The email to validate
   * @returns True if email is valid
   */
  validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  /**
   * Validate password strength
   * @param password - The password to validate
   * @returns True if password meets minimum requirements
   */
  validatePassword(password: string): boolean {
    return isPasswordValid(password)
  }

  /**
   * Validate phone format
   * @param phone - The phone to validate
   * @returns True if phone is valid
   */
  validatePhone(phone: string): boolean {
    // Basic phone validation - adjust regex based on requirements
    const phoneRegex = /^[+]?[1-9][\d]{0,15}$/
    return phoneRegex.test(phone.replace(/[\s\-()]/g, ''))
  }

  /**
   * Validate that passwords match
   * @param password - The password
   * @param confirmPassword - The confirmation password
   * @returns True if passwords match
   */
  validatePasswordsMatch(password: string, confirmPassword: string): boolean {
    return password === confirmPassword
  }
}

// Export singleton instance
export default new SignupService()
